
import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Wallet, Twitter, Share2, Award, CheckCircle, Brain } from 'lucide-react';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import InitiationWelcome from './initiation/InitiationWelcome';
import InitiationWallet from './initiation/InitiationWallet';
import InitiationPath from './initiation/InitiationPath';
import InitiationTwitter from './initiation/InitiationTwitter';
import InitiationSocial from './initiation/InitiationSocial';
import InitiationComplete from './initiation/InitiationComplete';
import CipherQuiz from '../CipherQuiz/CipherQuiz';

export type InitiationStep = 'welcome' | 'wallet' | 'path' | 'cipher' | 'twitter' | 'social' | 'complete';

interface MiniAppInitiationProps {
  onComplete: () => void;
}

interface InitiationData {
  walletAddress?: string;
  chainType?: string;
  chosenPath?: string;
  twitterHandle?: string;
  socialShared?: boolean;
  rank?: string;
  brocode?: string;
  userId?: string;
}

const MiniAppInitiation = ({ onComplete }: MiniAppInitiationProps) => {
  const [currentStep, setCurrentStep] = useState<InitiationStep>('welcome');
  const [completedSteps, setCompletedSteps] = useState<Set<InitiationStep>>(new Set());
  const [initiationData, setInitiationData] = useState<InitiationData>({});
  const [userData, setUserData] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { webApp, isTelegram } = useTelegramWebApp();

  const steps: { id: InitiationStep; title: string; icon: any }[] = [
    { id: 'welcome', title: 'Welcome', icon: Award },
    { id: 'wallet', title: 'Wallet', icon: Wallet },
    { id: 'path', title: 'Path', icon: CheckCircle },
    { id: 'cipher', title: 'Cipher', icon: Brain },
    { id: 'twitter', title: 'Twitter', icon: Twitter },
    { id: 'social', title: 'Share', icon: Share2 },
    { id: 'complete', title: 'Complete', icon: Award }
  ];

  useEffect(() => {
    if (isTelegram && webApp) {
      webApp.ready();
      webApp.expand();
    }
  }, [isTelegram, webApp]);

  const handleStepComplete = async (step: InitiationStep, data?: any) => {
    console.log('Step completed:', step, data);
    setCompletedSteps(prev => new Set([...prev, step]));
    
    if (data) {
      setInitiationData(prev => ({ ...prev, ...data }));
    }
    
    // Navigate based on completed step
    switch (step) {
      case 'welcome':
        setCurrentStep('wallet');
        break;
        
      case 'wallet':
        setCurrentStep('path');
        break;
        
      case 'path':
        // Navigate based on chosen path
        console.log('Path chosen:', data?.chosenPath);
        if (data?.chosenPath === 'cipher') {
          setCurrentStep('cipher');
        } else if (data?.chosenPath === 'twitter') {
          setCurrentStep('twitter');
        } else if (data?.chosenPath === 'liquidity') {
          // For liquidity, skip to social step (not implemented yet)
          setCurrentStep('social');
        } else {
          // Default fallback
          setCurrentStep('social');
        }
        break;
        
      case 'cipher':
        // After cipher, go to social
        setCurrentStep('social');
        break;
        
      case 'twitter':
        // After twitter, go to social
        setCurrentStep('social');
        break;
        
      case 'social':
        // After social, complete initiation
        await completeInitiation();
        break;
        
      default:
        // Move to next step
        const currentIndex = steps.findIndex(s => s.id === step);
        if (currentIndex < steps.length - 1 && currentIndex !== -1) {
          const nextStep = steps[currentIndex + 1];
          if (nextStep) {
            setCurrentStep(nextStep.id);
          }
        }
    }
  };

  const completeInitiation = async () => {
    if (!webApp?.initDataUnsafe?.user || !initiationData.walletAddress) {
      toast.error('Missing required initiation data');
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('Completing initiation with data:', initiationData);
      
      const { data, error } = await supabase.functions.invoke('complete-initiation', {
        body: {
          telegramId: webApp.initDataUnsafe.user.id.toString(),
          walletAddress: initiationData.walletAddress,
          chainType: initiationData.chainType,
          chosenPath: initiationData.chosenPath,
          twitterHandle: initiationData.twitterHandle
        }
      });

      if (error) throw error;

      if (data.success) {
        setUserData(data.userData);
        
        // Store BroCode in localStorage for authentication
        if (data.userData.brocode) {
          localStorage.setItem('brocode', data.userData.brocode);
          localStorage.setItem('user_brocode', data.userData.brocode);
          console.log(`✅ [STORAGE] BroCode stored during initiation: ${data.userData.brocode}`);
        }
        
        // Store the user ID for Twitter linking
        setInitiationData(prev => ({ ...prev, userId: data.userData.id }));
        setCurrentStep('complete');
        toast.success('Initiation completed successfully!');
      } else {
        throw new Error(data.error || 'Failed to complete initiation');
      }
    } catch (error) {
      console.error('Initiation completion error:', error);
      toast.error('Failed to complete initiation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCipherComplete = (rank: string, brocode: string) => {
    console.log('Cipher completed:', { rank, brocode });
    setInitiationData(prev => ({ ...prev, rank, brocode }));
    handleStepComplete('cipher', { rank, brocode });
  };

  const getStepStatus = (stepId: InitiationStep) => {
    if (completedSteps.has(stepId)) return 'completed';
    if (currentStep === stepId) return 'current';
    return 'pending';
  };

  const getVisibleSteps = () => {
    const baseSteps = ['welcome', 'wallet', 'path'];
    const pathSpecificSteps = [];
    
    if (initiationData.chosenPath === 'cipher') {
      pathSpecificSteps.push('cipher');
    } else if (initiationData.chosenPath === 'twitter') {
      pathSpecificSteps.push('twitter');
    }
    
    return [...baseSteps, ...pathSpecificSteps, 'social', 'complete'];
  };

  const renderStepContent = () => {
    if (isProcessing) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-foreground">Processing your initiation...</p>
        </div>
      );
    }

    switch (currentStep) {
      case 'welcome':
        return <InitiationWelcome onNext={() => handleStepComplete('welcome')} />;
        
      case 'wallet':
        return <InitiationWallet onComplete={(data) => handleStepComplete('wallet', data)} />;
        
      case 'path':
        return <InitiationPath onComplete={(data) => handleStepComplete('path', data)} />;
        
      case 'cipher':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <Brain className="mx-auto mb-4 text-primary" size={48} />
              <h3 className="text-xl font-bold text-foreground mb-2">Take The Cipher</h3>
              <p className="text-muted-foreground">
                Prove your knowledge of the Brotocol to earn your BroCode
              </p>
            </div>
            <CipherQuiz
              hasToken={false}
              walletAddress={initiationData.walletAddress as `0x${string}`}
              onComplete={handleCipherComplete}
              tableReady={true}
            />
          </div>
        );
        
      case 'twitter':
        return (
          <InitiationTwitter 
            onComplete={(data) => handleStepComplete('twitter', data)} 
          />
        );
        
      case 'social':
        return <InitiationSocial 
          onComplete={(data) => handleStepComplete('social', data)} 
          userData={userData || initiationData}
        />;
        
      case 'complete':
        return <InitiationComplete userData={userData} onFinish={onComplete} />;
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto">
        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            {steps.filter(step => getVisibleSteps().includes(step.id)).map((step) => {
              const status = getStepStatus(step.id);
              const Icon = step.icon;
              
              return (
                <div key={step.id} className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                      status === 'completed' ? 'bg-primary' :
                      status === 'current' ? 'bg-accent' : 'bg-muted'
                    }`}
                  >
                    <Icon size={16} className={
                      status === 'completed' ? 'text-primary-foreground' :
                      status === 'current' ? 'text-accent-foreground' : 'text-muted-foreground'
                    } />
                  </div>
                  <span className={`text-xs ${
                    status === 'completed' ? 'text-primary' :
                    status === 'current' ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${(completedSteps.size / getVisibleSteps().length) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MiniAppInitiation;
